import React, { useMemo, useState } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { PermissionButton } from "@/components/ui/permission-button";
import { DocumentUpload } from "@/components/ui/document-upload";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import {
  Users,
  CheckCircle,
  FileText,
  Download,
  Loader2,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  DollarSign,
  Upload,
  ExternalLink,
  Eye,
  EyeOff,
  Link2,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Employee, Client } from "@shared/schema";
import { useDocuments } from "@/hooks/useDocuments";
import { DocumentList } from "@/components/ui/document-list";
import { sortEmployeesByFullName } from "@/lib/employeeSort";
import {
  formatHeadcountBreakdown,
  getComplianceEmployeeStats,
  isCountedInHeadcount,
} from "@/lib/employeeHeadcount";
import { ComplianceChecklist, QuickTemplates, AuditLogViewer } from "@/components/compliance/ComplianceChecklists";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { asArray } from "@/lib/asArray";
import { useSubscriptionTier } from "@/hooks/useSubscriptionTier";
import { getTierLabel } from "@shared/subscriptionTiers";

interface ComplianceTabProps {
  employees: Employee[];
  client: Client | null;
  clientId: number;
}

interface GenerateFormButtonProps {
  employeeId: number;
  employeeName: string;
  onSuccess: () => void;
}

const GenerateFormButton = ({ employeeId, employeeName, onSuccess }: GenerateFormButtonProps) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const { toast } = useToast();

  const handleGenerate = async () => {
    setIsGenerating(true);
    try {
      const response = await fetch(`/api/employees/${employeeId}/generate-1095c`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ year: new Date().getFullYear() })
      });

      if (!response.ok) throw new Error('Failed to generate form');
      
      toast({
        title: "Form Generated Successfully",
        description: `1095-C form has been generated for ${employeeName}`,
      });

      onSuccess();
    } catch (error) {
      console.error('Error generating form:', error);
      toast({
        title: "Generation Failed",
        description: `Failed to generate 1095-C form for ${employeeName}`,
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <PermissionButton
      permission="clients:edit"
      whenDenied="hide"
      size="sm"
      variant="outline"
      onClick={handleGenerate}
      disabled={isGenerating}
    >
      {isGenerating ? (
        <>
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          Generating...
        </>
      ) : (
        <>
          <Download className="h-4 w-4 mr-2" />
          Generate 1095-C
        </>
      )}
    </PermissionButton>
  );
};

const ComplianceTab = ({ employees, client, clientId }: ComplianceTabProps) => {
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const [activeTab, setActiveTab] = useState("checklists");
  // /api/compliance/* is tier-gated server-side (403 below premium)
  const { hasFeature, getRequiredTier, isLoading: tierLoading } = useSubscriptionTier();
  const canUseComplianceWizard = !tierLoading && hasFeature("benefits_compliance_wizard");

  // Document upload state
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    type: 'file' as 'file' | 'link',
    linkUrl: '',
    visibility: 'internal' as 'internal' | 'employee',
    fileName: '',
    documentName: '',
    fileType: '' as string,
  });
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const COMPLIANCE_FILE_TYPES = [
    { value: 'sbc', label: 'Summary of Benefits and Coverage (SBC)' },
    { value: 'spd', label: 'Summary Plan Description (SPD)' },
    { value: 'form5500', label: 'Form 5500' },
    { value: 'wrap_document', label: 'Wrap Document' },
    { value: 'hipaa_notice', label: 'HIPAA Notice of Privacy Practices' },
    { value: 'cobra_notice', label: 'COBRA Notice' },
    { value: 'medicare_notice', label: 'Medicare Part D Notice' },
    { value: 'womens_health', label: "Women's Health and Cancer Rights Notice" },
    { value: 'chip_notice', label: 'CHIP Notice' },
    { value: 'exchange_notice', label: 'Exchange Notice' },
    { value: 'sar', label: 'Summary Annual Report (SAR)' },
    { value: 'plan_amendment', label: 'Plan Amendment' },
    { value: 'certificate', label: 'Certificate of Coverage' },
    { value: 'other', label: 'Other' },
  ];
  
  // Headcount (active + pending activation) — same set the header and the
  // HR / Open Enrollment tabs count (DEV-329). Terminated, COBRA etc. are out.
  const clientEmployees = useMemo(() => {
    const scopedEmployees = employees.filter(
      (employee) => employee.clientId === clientId && isCountedInHeadcount(employee)
    );
    return sortEmployeesByFullName(scopedEmployees);
  }, [employees, clientId]);
  // Shared with the employer Compliance page (DEV-362). Blank employment type
  // is shown separately and left out of the FTE math.
  const stats = getComplianceEmployeeStats(clientEmployees);
  const headcount = stats.headcount;
  const totalEmployees = headcount.total;
  const fullTimeEmployees = stats.fullTime;
  const partTimeEmployees = stats.partTime;
  const notSetEmployees = stats.notSet;
  const fullTimeEquivalents = stats.partTimeFte;
  const totalFTE = stats.totalFte;

  const fullTimeEquivalentsDisplay = Number(fullTimeEquivalents.toFixed(2));
  const totalFTEDisplay = Number(totalFTE.toFixed(2));

  // Fetch stored compliance status from backend (calculated annually on Jan 1)
  const { data: complianceStatus } = useQuery<{
    aleStatus: boolean | null;
    cobraStatus: boolean | null;
    aleStatusDeterminedAt: Date | null;
    cobraStatusDeterminedAt: Date | null;
  }>({
    queryKey: ["/api/compliance/status", clientId],
    enabled: !!clientId,
  });

  // ALE status comes from the ACA engine (prior-year FT + FTE), never from a
  // local headcount formula. Estimated when prior-year hours are incomplete.
  const acaTaxYear = new Date().getFullYear();
  const { data: acaDashboard } = useQuery<{ ale?: { isAle: boolean; averageFte: number; estimated: boolean; basisYear: number; source: string } }>({
    queryKey: ["/api/aca/dashboard", clientId, acaTaxYear],
    queryFn: async () => {
      const res = await fetch(`/api/aca/dashboard?clientId=${clientId}&taxYear=${acaTaxYear}`, { credentials: "include" });
      if (!res.ok) return {};
      return res.json();
    },
    enabled: !!clientId,
  });
  const isALE = !!acaDashboard?.ale?.isAle;
  const aleFteDisplay = acaDashboard?.ale
    ? `${acaDashboard.ale.averageFte} FT + FTE (${acaDashboard.ale.basisYear}${acaDashboard.ale.estimated ? ", estimated" : ""})`
    : "Not computed";
  const isHIPAASubject = totalEmployees >= 1;
  const isERISASubject = totalEmployees >= 1;
  const isFederalCOBRASubject = complianceStatus?.cobraStatus ?? (totalEmployees >= 20);
  const isFMLASubject = totalEmployees >= 50;
  const isACAReportingSubject = isALE;

  const {
    documents: complianceDocs = [],
    error: docsError,
    refreshDocuments,
  } = useDocuments({
    ownerType: "client",
    clientId: client?.id as number,
    category: "compliance",
    autoRefresh: true,
  });

  // Fetch evidence with checklist details
  const { data: evidenceData, refetch: refetchEvidence } = useQuery<any[]>({
    queryKey: ['/api/compliance/evidence/with-details', client?.id],
    queryFn: async () => {
      if (!client?.id) return [];
      const res = await fetch(`/api/compliance/evidence/with-details?clientId=${client.id}`, { credentials: 'include' });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!client?.id,
  });
  const evidenceWithDetails = asArray<any>(evidenceData);

  const handleUploadDocument = async () => {
    if (uploadForm.type === 'link' && !uploadForm.linkUrl) {
      toast({ title: "Error", description: "Please enter a URL", variant: "destructive" });
      return;
    }
    if (uploadForm.type === 'file' && !uploadFile) {
      toast({ title: "Error", description: "Please select a file", variant: "destructive" });
      return;
    }

    setIsUploading(true);
    try {
      if (uploadForm.type === 'link') {
        await apiRequest('POST', '/api/compliance/evidence', {
          clientId: client?.id,
          type: 'link',
          linkUrl: uploadForm.linkUrl,
          fileName: uploadForm.documentName || uploadForm.linkUrl,
          documentName: uploadForm.documentName || uploadForm.linkUrl,
          fileType: uploadForm.fileType || null,
          visibility: uploadForm.visibility,
        });
      } else if (uploadFile) {
        const formData = new FormData();
        formData.append('file', uploadFile);
        formData.append('clientId', String(client?.id));
        formData.append('visibility', uploadForm.visibility);
        formData.append('documentName', uploadForm.documentName || uploadFile.name);
        formData.append('fileType', uploadForm.fileType || '');

        const res = await fetch('/api/compliance/evidence/upload', {
          method: 'POST',
          credentials: 'include',
          body: formData,
        });
        if (!res.ok) throw new Error('Upload failed');
      }

      refetchEvidence();
      setShowUploadDialog(false);
      setUploadForm({ type: 'file', linkUrl: '', visibility: 'internal', fileName: '', documentName: '', fileType: '' });
      setUploadFile(null);
      toast({ title: "Success", description: "Document uploaded successfully" });
    } catch (error: any) {
      toast({ title: "Error", description: error.message || "Upload failed", variant: "destructive" });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownloadEvidence = async (ev: any) => {
    if (ev.type === 'link' && ev.linkUrl) {
      window.open(ev.linkUrl, '_blank');
    } else if (ev.s3Key) {
      const res = await fetch(`/api/compliance/evidence/${ev.id}/download`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        window.open(data.url, '_blank');
      }
    }
  };

  // Checklist completion summary by area
  const { data: checklistSummary = {} } = useQuery<Record<string, { total: number; completed: number; isComplete: boolean }>>({
    queryKey: ["/api/compliance/checklist-summary", client?.id],
    enabled: !!client?.id,
    queryFn: async () => {
      const res = await fetch(`/api/compliance/checklist-summary?clientId=${client?.id}`, { credentials: 'include' });
      if (!res.ok) return {};
      return res.json();
    }
  });

  const complianceRequirements = [
    { name: "Applicable Large Employer (ALE)", description: "50+ full-time equivalent employees", isSubject: isALE, threshold: "50 FT + FTE (prior year)", current: aleFteDisplay, details: "Must offer minimum essential coverage or pay penalty", areaKey: "aca" },
    { name: "HIPAA", description: "Health Insurance Portability and Accountability Act", isSubject: isHIPAASubject, threshold: "1+ employees", current: `${totalEmployees} employees`, details: "Privacy and security rules for health information", areaKey: "hipaa" },
    { name: "ERISA", description: "Employee Retirement Income Security Act", isSubject: isERISASubject, threshold: "1+ employees", current: `${totalEmployees} employees`, details: "Fiduciary responsibilities and reporting requirements", areaKey: "erisa" },
    { name: "Federal COBRA", description: "Consolidated Omnibus Budget Reconciliation Act", isSubject: isFederalCOBRASubject, threshold: "20+ employees", current: `${totalEmployees} employees`, details: "Continuation coverage rights for terminated employees", areaKey: "cobra" },
    { name: "FMLA", description: "Family and Medical Leave Act", isSubject: isFMLASubject, threshold: "50+ employees", current: `${totalEmployees} employees`, details: "Unpaid leave protection for eligible employees", areaKey: "fmla" },
    { name: "ACA Employer Reporting", description: "Forms 1094-C and 1095-C filing requirements", isSubject: isACAReportingSubject, threshold: "ALE (50+ FT + FTE)", current: aleFteDisplay, details: "Annual reporting of health coverage offered", areaKey: "aca" },
  ];

  const getCardStyle = (req: typeof complianceRequirements[0]) => {
    if (!req.isSubject) {
      return "border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900/20";
    }
    const summary = checklistSummary[req.areaKey];
    const isComplete = summary?.isComplete || false;
    if (isComplete) {
      return "border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950/20";
    }
    return "border-orange-200 bg-orange-50 dark:border-orange-800 dark:bg-orange-950/20";
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">Compliance</h3>
      </div>

      <Card className="w-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Employee Statistics Overview
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-6">
            <div className="text-center p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border" data-testid="compliance-total-employees">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{totalEmployees}</div>
              <div className="text-sm font-medium text-gray-600 dark:text-gray-300">Total Employees</div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{formatHeadcountBreakdown(headcount)}</div>
            </div>
            <div className="text-center p-4 bg-green-50 dark:bg-green-950/20 rounded-lg border" data-testid="compliance-full-time">
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">{fullTimeEmployees.length}</div>
              <div className="text-sm font-medium text-gray-600 dark:text-gray-300">Full-Time</div>
            </div>
            <div className="text-center p-4 bg-orange-50 dark:bg-orange-950/20 rounded-lg border" data-testid="compliance-part-time">
              <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">{partTimeEmployees.length}</div>
              <div className="text-sm font-medium text-gray-600 dark:text-gray-300">Part-Time</div>
            </div>
            <div className="text-center p-4 bg-gray-50 dark:bg-gray-900/20 rounded-lg border" data-testid="compliance-not-set">
              <div className="text-2xl font-bold text-gray-600 dark:text-gray-300">{notSetEmployees.length}</div>
              <div className="text-sm font-medium text-gray-600 dark:text-gray-300">Type Not Set</div>
              {notSetEmployees.length > 0 && (
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Set an employment type to include in FTE
                </div>
              )}
            </div>
            <div className="text-center p-4 bg-purple-50 dark:bg-purple-950/20 rounded-lg border">
              <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">{fullTimeEquivalentsDisplay}</div>
              <div className="text-sm font-medium text-gray-600 dark:text-gray-300">FTE (Part-Time)</div>
            </div>
            <div className="text-center p-4 bg-teal-50 dark:bg-teal-950/20 rounded-lg border">
              <div className="text-2xl font-bold text-teal-600 dark:text-teal-400">{totalFTEDisplay}</div>
              <div className="text-sm font-medium text-gray-600 dark:text-gray-300">Total FTE</div>
            </div>
          </div>

          {isALE && (
            <div className="mt-4 p-3 bg-orange-100 dark:bg-orange-900/30 rounded border border-orange-200 dark:border-orange-800">
              <p className="text-sm font-medium text-orange-800 dark:text-orange-200">
                ⚠️ Applicable Large Employer (ALE) Status
              </p>
              <p className="text-xs text-orange-700 dark:text-orange-300 mt-1">
                This employer may be subject to ACA employer mandate penalties if minimum essential coverage and minimum value plans are not offered, or if the plan is deemed unaffordable according to ACA standards.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="w-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5" />
            Compliance Requirements Validator
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {complianceRequirements.map((req, index) => {
              const summary = checklistSummary[req.areaKey];
              const isComplete = summary?.isComplete || false;
              const hasChecklist = summary && summary.total > 0;
              
              return (
                <div
                  key={index}
                  className={`p-4 rounded-lg border-2 ${getCardStyle(req)}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-semibold text-sm">{req.name}</h4>
                    <div className="flex flex-col items-end gap-1">
                      <Badge variant={req.isSubject ? "secondary" : "outline"} className="text-xs">
                        {req.isSubject ? "Subject" : "Not Subject"}
                      </Badge>
                      {req.isSubject && hasChecklist && (
                        <Badge
                          variant={isComplete ? "default" : "secondary"}
                          className={`text-xs ${isComplete ? "bg-green-600 hover:bg-green-700" : "bg-orange-500 hover:bg-orange-600 text-white"}`}
                        >
                          {isComplete ? "Complete" : "Incomplete"}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-300 mb-2">{req.description}</p>
                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Threshold:</span>
                      <span className="font-medium">{req.threshold}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Current:</span>
                      <span className="font-medium">{req.current}</span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2 italic">{req.details}</p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between mb-4">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid grid-cols-2">
            <TabsTrigger value="checklists">Checklists</TabsTrigger>
            <TabsTrigger value="documents">Documents</TabsTrigger>
          </TabsList>

        <TabsContent value="checklists" className="space-y-4 mt-4">
          {tierLoading ? null : !canUseComplianceWizard ? (
            <div className="rounded-lg border p-6 text-sm text-muted-foreground" data-testid="compliance-checklists-upgrade">
              Compliance checklists require the {getTierLabel(getRequiredTier("benefits_compliance_wizard"))} subscription tier or higher.
            </div>
          ) : (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Compliance Checklists
                </span>
                <AuditLogViewer clientId={clientId} />
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {isFederalCOBRASubject && <ComplianceChecklist clientId={clientId} areaKey="cobra" />}
              <ComplianceChecklist clientId={clientId} areaKey="hipaa" />
              <ComplianceChecklist clientId={clientId} areaKey="erisa" />
              {isALE && <ComplianceChecklist clientId={clientId} areaKey="aca" />}
              {isFMLASubject && <ComplianceChecklist clientId={clientId} areaKey="fmla" />}
            </CardContent>
          </Card>
          )}
        </TabsContent>

        <TabsContent value="documents" className="space-y-4 mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Compliance Documents</CardTitle>
              {/* Upload posts to /api/compliance/evidence — tier-gated */}
              {canUseComplianceWizard && (
                <Button variant="outline" size="sm" onClick={() => setShowUploadDialog(true)}>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Document
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {docsError && (
                <div className="text-sm text-red-600 mb-4">{docsError}</div>
              )}
              
              {evidenceWithDetails.length === 0 && (complianceDocs || []).length === 0 ? (
                <p className="text-sm text-muted-foreground">No compliance documents found. Upload documents or attach evidence to checklist items.</p>
              ) : (
                <div className="space-y-4">
                  {/* Checklist Evidence */}
                  {evidenceWithDetails.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="text-sm font-medium text-muted-foreground">Checklist Evidence</h4>
                      {evidenceWithDetails.map((ev: any) => (
                        <div key={`ev-${ev.id}`} className="flex items-center justify-between p-3 border rounded hover:bg-muted/50">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            {ev.type === 'link' ? (
                              <Link2 className="h-4 w-4 text-blue-500 flex-shrink-0" />
                            ) : (
                              <FileText className="h-4 w-4 text-gray-500 flex-shrink-0" />
                            )}
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-sm truncate">{ev.fileName || ev.linkUrl || 'Document'}</p>
                              <div className="flex items-center gap-2 flex-wrap">
                                {ev.areaKey && (
                                  <Badge variant="outline" className="text-xs uppercase">{ev.areaKey}</Badge>
                                )}
                                {ev.formType && (
                                  <Badge variant="secondary" className="text-xs">{ev.formType}</Badge>
                                )}
                                {ev.checklistItemTitle && !ev.formType && (
                                  <span className="text-xs text-muted-foreground">{ev.checklistItemTitle}</span>
                                )}
                                <span className="text-xs text-muted-foreground">
                                  {ev.createdAt ? new Date(ev.createdAt).toLocaleDateString() : ''}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {ev.visibility === 'employee' ? (
                              <Badge variant="default" className="text-xs flex items-center gap-1">
                                <Eye className="h-3 w-3" /> Employee
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-xs flex items-center gap-1">
                                <EyeOff className="h-3 w-3" /> Internal
                              </Badge>
                            )}
                            <Button variant="ghost" size="sm" onClick={() => handleDownloadEvidence(ev)}>
                              {ev.type === 'link' ? <ExternalLink className="h-4 w-4" /> : <Download className="h-4 w-4" />}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Legacy Compliance Documents */}
                  {(complianceDocs || []).length > 0 && (
                    <div className="space-y-2">
                      <h4 className="text-sm font-medium text-muted-foreground">Uploaded Documents</h4>
                      <DocumentList
                        documents={complianceDocs as any}
                        onDocumentUpdate={refreshDocuments}
                      />
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      </div>

      {/* Upload Document Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Compliance Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Document Type</Label>
              <RadioGroup 
                value={uploadForm.type} 
                onValueChange={(v) => setUploadForm(f => ({ ...f, type: v as 'file' | 'link' }))}
                className="flex gap-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="file" id="broker-type-file" />
                  <Label htmlFor="broker-type-file">File Upload</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="link" id="broker-type-link" />
                  <Label htmlFor="broker-type-link">External Link</Label>
                </div>
              </RadioGroup>
            </div>

            {uploadForm.type === 'link' ? (
              <>
                <div className="space-y-2">
                  <Label>Link URL</Label>
                  <Input
                    placeholder="https://example.com/document"
                    value={uploadForm.linkUrl}
                    onChange={(e) => setUploadForm(f => ({ ...f, linkUrl: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Document Name</Label>
                  <Input
                    placeholder="Enter document name"
                    value={uploadForm.documentName}
                    onChange={(e) => setUploadForm(f => ({ ...f, documentName: e.target.value }))}
                  />
                </div>
              </>
            ) : (
              <>
                <div className="space-y-2">
                  <Label>Select File</Label>
                  <Input
                    type="file"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                    onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                  />
                  <p className="text-xs text-muted-foreground">Accepted: PDF, Word, Excel, Images</p>
                </div>
                <div className="space-y-2">
                  <Label>Document Name</Label>
                  <Input
                    placeholder="Enter document name (optional)"
                    value={uploadForm.documentName}
                    onChange={(e) => setUploadForm(f => ({ ...f, documentName: e.target.value }))}
                  />
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label>File Type</Label>
              <Select 
                value={uploadForm.fileType} 
                onValueChange={(v) => setUploadForm(f => ({ ...f, fileType: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select document type" />
                </SelectTrigger>
                <SelectContent>
                  {COMPLIANCE_FILE_TYPES.map(ft => (
                    <SelectItem key={ft.value} value={ft.value}>{ft.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between p-3 border rounded-lg bg-muted/30">
              <div>
                <p className="text-sm font-medium">Employee Visibility</p>
                <p className="text-xs text-muted-foreground">Allow employees to view this document</p>
              </div>
              <Switch
                checked={uploadForm.visibility === 'employee'}
                onCheckedChange={(checked) => setUploadForm(f => ({ ...f, visibility: checked ? 'employee' : 'internal' }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadDialog(false)}>Cancel</Button>
            <Button onClick={handleUploadDocument} disabled={isUploading}>
              {isUploading ? "Uploading..." : "Upload"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ComplianceTab;
